/**
 * Electrical reference data — real published tables, bundled offline.
 *
 * Sources (values transcribed, no network needed at runtime):
 *  - NEC Chapter 9, Table 4  — total cross-sectional area of conduit (in²)
 *  - NEC Chapter 9, Table 5  — approximate area of insulated conductors (in²)
 *  - NEC Chapter 9, Table 8  — conductor properties (weight lb/1000 ft)
 *  - NEC Table 310.16        — allowable ampacity, copper, 60/75/90 °C
 *  - NEC Chapter 9, Table 1  — conduit fill percentages
 *
 * Prices and labor units are seed values (typical shop numbers) meant to be
 * tuned by the user; the physical tables above are fixed reference data.
 */

export const TRADE_SIZES = [
  '1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', '3-1/2"', '4"',
] as const;
export type TradeSize = (typeof TRADE_SIZES)[number];

export type RacewayId = "emt" | "imc" | "rmc" | "pvc40" | "pvc80" | "fmc";

export interface RacewayType {
  id: RacewayId;
  label: string;
  short: string;
  /** NEC Ch.9 Table 4 — total internal area, in², by trade size */
  area: Partial<Record<TradeSize, number>>;
  /** Seed material price, $/ft */
  price: Partial<Record<TradeSize, number>>;
  /** Seed labor, crew-hours per foot installed (includes straps & couplings) */
  hrs: Partial<Record<TradeSize, number>>;
}

const row = (vals: number[]) =>
  Object.fromEntries(TRADE_SIZES.map((s, i) => [s, vals[i]])) as unknown as Record<TradeSize, number>;


export const RACEWAYS: Record<RacewayId, RacewayType> = {
  emt: {
    id: "emt", label: "EMT (steel)", short: "EMT",
    area: row([0.304, 0.533, 0.864, 1.496, 2.036, 3.356, 5.858, 8.846, 11.545, 14.753]),
    price: row([0.68, 0.92, 1.35, 1.95, 2.45, 3.10, 5.60, 7.20, 9.60, 12.40]),
    hrs: row([0.040, 0.045, 0.055, 0.070, 0.080, 0.095, 0.120, 0.145, 0.165, 0.190]),
  },
  imc: {
    id: "imc", label: "IMC (intermediate metal)", short: "IMC",
    area: row([0.342, 0.586, 0.959, 1.647, 2.225, 3.630, 5.135, 7.922, 10.584, 13.631]),
    price: row([1.85, 2.35, 3.40, 4.60, 5.60, 7.40, 12.00, 15.50, 20.00, 25.50]),
    hrs: row([0.055, 0.062, 0.075, 0.095, 0.110, 0.130, 0.165, 0.200, 0.230, 0.265]),
  },
  rmc: {
    id: "rmc", label: "RMC (rigid galvanized)", short: "RMC",
    area: row([0.314, 0.549, 0.887, 1.526, 2.071, 3.408, 4.866, 7.499, 10.010, 12.882]),
    price: row([2.40, 3.10, 4.40, 5.90, 7.20, 9.60, 15.00, 19.50, 25.00, 31.00]),
    hrs: row([0.060, 0.070, 0.085, 0.105, 0.125, 0.150, 0.190, 0.230, 0.265, 0.305]),
  },
  pvc40: {
    id: "pvc40", label: "PVC Schedule 40", short: "PVC 40",
    area: row([0.285, 0.508, 0.832, 1.453, 1.986, 3.291, 4.695, 7.268, 9.737, 12.554]),
    price: row([0.42, 0.58, 0.90, 1.25, 1.60, 2.10, 3.40, 4.40, 5.90, 7.40]),
    hrs: row([0.030, 0.034, 0.040, 0.050, 0.058, 0.070, 0.090, 0.108, 0.125, 0.145]),
  },
  pvc80: {
    id: "pvc80", label: "PVC Schedule 80", short: "PVC 80",
    area: row([0.217, 0.409, 0.688, 1.237, 1.711, 2.874, 4.119, 6.442, 8.688, 11.258]),
    price: row([0.72, 0.98, 1.45, 2.05, 2.70, 3.60, 5.80, 7.60, 9.90, 12.60]),
    hrs: row([0.034, 0.038, 0.045, 0.056, 0.065, 0.078, 0.100, 0.120, 0.140, 0.160]),
  },
  fmc: {
    id: "fmc", label: "FMC (flexible metal)", short: "Flex",
    area: row([0.317, 0.533, 0.817, 1.277, 1.858, 3.269, 4.909, 7.069, 9.621, 12.566]),
    price: row([0.95, 1.30, 2.10, 3.20, 4.10, 5.60, 9.20, 12.50, 16.00, 20.00]),
    hrs: row([0.035, 0.040, 0.048, 0.060, 0.070, 0.085, 0.110, 0.132, 0.152, 0.175]),
  },
};

export const AWG_SIZES = [
  "14", "12", "10", "8", "6", "4", "3", "2", "1", "1/0", "2/0", "3/0", "4/0",
  "250", "300", "350", "400", "500",
] as const;
export type AwgSize = (typeof AWG_SIZES)[number];

const awgRow = (vals: number[]) =>
  Object.fromEntries(AWG_SIZES.map((s, i) => [s, vals[i]])) as Record<AwgSize, number>;

export type InsulationId = "thhn" | "xhhw";

export interface WireType {
  id: InsulationId;
  label: string;
  /** NEC Ch.9 Table 5 — approximate area including insulation, in² */
  area: Record<AwgSize, number>;
  /** Seed material price, $/ft per conductor (copper) */
  price: Record<AwgSize, number>;
  /** Seed labor, hours per foot per conductor pulled */
  hrs: Record<AwgSize, number>;
}

/** NEC Ch.9 Table 8 — copper conductor weight, lb per 1000 ft (bare) */
export const CU_WEIGHT_LB_PER_KFT = awgRow([
  12.4, 19.8, 31.4, 49.98, 79.44, 126.3, 159.3, 200.9, 253.3,
  319.5, 402.8, 507.8, 640.5, 756, 907, 1057, 1210, 1510,
]);

/** NEC Table 310.16 — allowable ampacity, copper, 60/75/90 °C columns */
export const AMPACITY_CU: Record<AwgSize, { c60: number; c75: number; c90: number }> =
  Object.fromEntries(
    AWG_SIZES.map((s, i) => [s, {
      c60: [15, 20, 30, 40, 55, 70, 85, 95, 110, 125, 145, 165, 195, 215, 240, 260, 280, 320][i],
      c75: [20, 25, 35, 50, 65, 85, 100, 115, 130, 150, 175, 200, 230, 255, 285, 310, 335, 380][i],
      c90: [25, 30, 40, 55, 75, 95, 115, 130, 145, 170, 195, 225, 260, 290, 320, 350, 380, 430][i],
    }]),
  ) as Record<AwgSize, { c60: number; c75: number; c90: number }>;

export const WIRE_TYPES: Record<InsulationId, WireType> = {
  thhn: {
    id: "thhn", label: "THHN/THWN-2 Cu",
    area: awgRow([
      0.0097, 0.0133, 0.0211, 0.0366, 0.0507, 0.0824, 0.0973, 0.1158, 0.1562,
      0.1855, 0.2223, 0.2679, 0.3237, 0.3970, 0.4608, 0.5242, 0.5863, 0.7073,
    ]),
    price: awgRow([
      0.16, 0.24, 0.38, 0.62, 0.95, 1.45, 1.80, 2.25, 2.80,
      3.45, 4.30, 5.35, 6.70, 8.10, 9.70, 11.20, 12.80, 15.90,
    ]),
    hrs: awgRow([
      0.0030, 0.0033, 0.0038, 0.0048, 0.0058, 0.0070, 0.0078, 0.0088, 0.0100,
      0.0112, 0.0126, 0.0142, 0.0160, 0.0180, 0.0200, 0.0220, 0.0240, 0.0275,
    ]),
  },
  xhhw: {
    id: "xhhw", label: "XHHW-2 Cu",
    area: awgRow([
      0.0139, 0.0181, 0.0243, 0.0437, 0.0590, 0.0814, 0.0962, 0.1146, 0.1534,
      0.1825, 0.2190, 0.2642, 0.3197, 0.3904, 0.4536, 0.5166, 0.5782, 0.6984,
    ]),
    price: awgRow([
      0.20, 0.29, 0.45, 0.72, 1.08, 1.62, 2.00, 2.50, 3.10,
      3.80, 4.75, 5.90, 7.35, 8.90, 10.60, 12.30, 14.00, 17.40,
    ]),
    hrs: awgRow([
      0.0030, 0.0033, 0.0038, 0.0048, 0.0058, 0.0070, 0.0078, 0.0088, 0.0100,
      0.0112, 0.0126, 0.0142, 0.0160, 0.0180, 0.0200, 0.0220, 0.0240, 0.0275,
    ]),
  },
};

/** NEC Chapter 9, Table 1 — maximum percent fill by conductor count */
export const fillLimitPct = (conductorCount: number) =>
  conductorCount <= 0 ? 0 : conductorCount === 1 ? 53 : conductorCount === 2 ? 31 : 40;

export interface FillResult {
  wireArea: number;
  conduitArea: number;
  pct: number;
  limit: number;
  ok: boolean;
}

export function conduitFill(
  raceway: RacewayId,
  size: TradeSize,
  conductors: { insulation: InsulationId; awg: AwgSize; count: number }[],
): FillResult | null {
  const conduitArea = RACEWAYS[raceway].area[size];
  if (!conduitArea) return null;
  let wireArea = 0;
  let count = 0;
  for (const c of conductors) {
    if (!(c.count > 0)) continue;
    wireArea += WIRE_TYPES[c.insulation].area[c.awg] * c.count;
    count += c.count;
  }
  const limit = fillLimitPct(count);
  const pct = conduitArea > 0 ? (wireArea / conduitArea) * 100 : 0;
  return { wireArea, conduitArea, pct, limit, ok: count === 0 || pct <= limit };
}

/** Smallest trade size of a raceway that legally fits the given conductors */
export function smallestConduit(
  raceway: RacewayId,
  conductors: { insulation: InsulationId; awg: AwgSize; count: number }[],
): TradeSize | null {
  for (const s of TRADE_SIZES) {
    const f = conduitFill(raceway, s, conductors);
    if (f && f.ok && f.wireArea > 0) return s;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* devices & gear — seed product prices and labor units                */
/* ------------------------------------------------------------------ */

export interface DeviceSpec {
  label: string;
  /** Seed material price, $ each */
  price: number;
  /** Seed labor, crew-hours each installed */
  hrs: number;
  /** short spec line for the material database */
  spec: string;
  /** true for panels, gear and equipment (grouped apart from devices) */
  gear?: boolean;
}

/** Devices, boxes and gear priced per each. Seed shop numbers, user editable. */
export const DEVICES: Record<string, DeviceSpec> = {
  "duplex": { label: "Duplex receptacle 20A", price: 3.2, hrs: 0.35, spec: "NEMA 5-20R, spec grade" },
  "gfci": { label: "GFCI receptacle 20A", price: 22, hrs: 0.4, spec: "Self-test, weather resistant" },
  "recept-208": { label: "Receptacle, 30A 250V", price: 28, hrs: 0.6, spec: "NEMA 6-30R" },
  "switch-1p": { label: "Switch, single-pole", price: 2.8, hrs: 0.3, spec: "20A 120/277V spec grade" },
  "switch-3w": { label: "Switch, 3-way", price: 6.5, hrs: 0.4, spec: "20A 120/277V spec grade" },
  "occ-sensor": { label: "Occupancy sensor", price: 48, hrs: 0.55, spec: "Dual tech, wall mount" },
  "box-4sq": { label: '4" square box + ring', price: 5.4, hrs: 0.35, spec: '4-11/16" steel, 2-1/8" deep' },
  "box-masonry": { label: "Masonry box", price: 6.8, hrs: 0.45, spec: "1-gang, 3-1/2\" deep" },
  "fixture-2x4": { label: "2x4 LED troffer (install)", price: 0, hrs: 0.75, spec: "Labor only — fixture by others" },
  "fixture-strip": { label: "LED strip fixture (install)", price: 0, hrs: 0.6, spec: "Labor only — fixture by others" },
  "hi-bay": { label: "LED high bay (install)", price: 0, hrs: 1.25, spec: "Labor only — fixture by others" },
  "disconnect-60": { label: "Disconnect, 60A NF", price: 78, hrs: 1.2, spec: "NEMA 3R, non-fused", gear: true },
  "disconnect-100": { label: "Disconnect, 100A fused", price: 210, hrs: 1.8, spec: "NEMA 3R, fusible", gear: true },
  "starter": { label: "Motor starter, size 1", price: 320, hrs: 2.5, spec: "NEMA size 1, 120V coil", gear: true },
  "breaker-1p": { label: "Breaker, 1-pole 20A", price: 8, hrs: 0.25, spec: "Bolt-on, 10 kAIC", gear: true },
  "breaker-2p": { label: "Breaker, 2-pole", price: 42, hrs: 0.35, spec: "Bolt-on, 10 kAIC", gear: true },
  "breaker-3p": { label: "Breaker, 3-pole", price: 165, hrs: 0.6, spec: "Bolt-on, 22 kAIC", gear: true },
  "panel-24": { label: "Panelboard, 24-ckt (set/trim)", price: 850, hrs: 6, spec: "125A MLO, 208Y/120V", gear: true },
  "panel-42": { label: "Panelboard, 42-ckt (set/trim)", price: 1450, hrs: 8, spec: "225A MLO, 208Y/120V", gear: true },
  "panel-42-mcb": { label: "Panelboard, 42-ckt with main breaker", price: 2150, hrs: 9, spec: "400A MCB, 208Y/120V", gear: true },
  "mdp-600": { label: "Distribution panel, 600A", price: 6800, hrs: 24, spec: "600A MLO, 480Y/277V, NEMA 1", gear: true },
  "switchboard-1200": { label: "Switchboard, 1200A", price: 24500, hrs: 60, spec: "1200A, 480Y/277V, front accessible", gear: true },
  "xfmr-45": { label: "Dry transformer, 45 kVA", price: 3200, hrs: 12, spec: "480 - 208Y/120V, floor/wall mount", gear: true },
  "xfmr-75": { label: "Dry transformer, 75 kVA", price: 4600, hrs: 16, spec: "480 - 208Y/120V, floor mount", gear: true },
  "meter-socket": { label: "Meter socket, 200A", price: 165, hrs: 3, spec: "Ringless, utility approved", gear: true },
  "ground-rod": { label: "Ground rod + clamp", price: 28, hrs: 0.8, spec: '5/8" x 8 ft copper clad, NEC 250.52', gear: true },
  "ground-bar": { label: "Ground bar, wall mount", price: 45, hrs: 0.7, spec: "Copper, insulated standoffs", gear: true },
  "term-lug": { label: "Terminate lug, large conductor", price: 6, hrs: 0.35, spec: "Compression, #1/0 and up", gear: true },
};

export const DEVICE_KEYS = Object.keys(DEVICES);
